/* eslint-disable @typescript-eslint/ban-ts-comment */
import Axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { attachSentryAxiosErrorInterceptor } from '../../src/observability/axios-sentry';

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
const defaultApiBaseUrl = import.meta.env.PROD
  ? 'https://api.thedevfactory.ai'
  : 'http://localhost:5001';
export const API_BASE_URL = configuredApiBaseUrl || defaultApiBaseUrl;
export const API_TIMEOUT_MS = 15_000;

export const AXIOS_INSTANCE = Axios.create({ 
  withCredentials:true,// enabling the browser to include relevant cookies
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  timeoutErrorMessage: `API request timed out after ${API_TIMEOUT_MS / 1000} seconds.`,
})

AXIOS_INSTANCE.interceptors.request.use(
    (config) => {
      try {
        const orgId = localStorage.getItem("currentOrganization")
        if (orgId) {
          // You can send it as a header (recommended)
          config.headers["x-organization-id"] = orgId
        }
      } catch (err) {
        console.warn("Could not attach organization_id", err)
      }
      return config
    },
    (error) => Promise.reject(error)
  )

/**
 * The backend refused the org we sent (a membership removed mid-session, or an
 * ID the route gate hasn't re-checked yet). Forget it and reload into the
 * picker with that org hidden; the reload also drops everything cached under
 * it. Only the first 403 of a burst acts, since it clears the stored value.
 */
export function handleOrganizationRejected() {
  const rejected = localStorage.getItem("currentOrganization")
  if (!rejected) return
  localStorage.removeItem("currentOrganization")
  const search = new URLSearchParams({
    rejected,
    redirect: window.location.pathname + window.location.search,
  })
  window.location.assign(`/select-organization?${search}`)
}

// PostgresBackend requireAuth's exact wording; other 403s (e.g. admin-only) must not match.
export const isOrganizationRejection = (status: number | undefined, data: unknown) =>
  status === 403 && (data as { error?: unknown } | null)?.error === "No permission for this organization"

AXIOS_INSTANCE.interceptors.response.use(undefined, (error) => {
  if (Axios.isAxiosError(error) && isOrganizationRejection(error.response?.status, error.response?.data)) {
    handleOrganizationRejected()
  }
  return Promise.reject(error)
})

attachSentryAxiosErrorInterceptor(AXIOS_INSTANCE)

// add a second `options` argument here if you want to pass extra options to each generated query
export const customClient = <T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
    ): Promise<T> => {
    const source = Axios.CancelToken.source();
    //const authPermissionsConfig=permissionsMiddleware({config:authConfig})
    const promise = AXIOS_INSTANCE({
        ...config,
        ...options,
        cancelToken: source.token,
    }).then(({ data }) => data)
    .catch((err)=>{
        throw err
    })
        

    // @ts-ignore
    promise.cancel = () => {
        source.cancel('Query was cancelled');
    };

    return promise;
};

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
 
